import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as R from "ramda";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import { Select } from "antd";
import { DebounceInput } from "react-debounce-input";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  settingsHandleForm,
  settingsChangeLanguage,
} from "./settings.redux";
import { updateSharedSettings } from "../login/userSession.redux";
import {
  setShowJobServiceModal,
  setShowSalespersonModal,
  setShowLocationModal,
  setShowLocationEditModal,
  setShowSalespersonEditModal,
  setShowJobEditServiceModal,
  deleteJobService,
  updateJobServices,
  deleteSalesPerson,
  deleteLocation,
} from "../plugins/plugin.redux";
import { DraggableService } from "../profile/DraggableService";
import { ModalJobService } from "../modal/ModalJobService";
import { ModalJobEditService } from "../modal/ModalJobEditService";
import { ModalSalesPerson } from "../modal/ModalSalesPerson";
import { ModalSalespersonEdit } from "../modal/ModalSalespersonEdit";
import { ModalLocation } from "../modal/ModalLocation";
import { ModalLocationEdit } from "../modal/ModalLocationEdit";
import UpdatingSpinner from "../ui/UpdatingSpinner";
import { WIZARD_ENABLED } from "../wizard/wizardSteps";
import SpectreposStatusPanel from "../plugins/SpectreposStatusPanel";
import ProvisioningProgress from "../plugins/ProvisioningProgress";

const PAGE_LINKS = {
  manual:
    "https://wiki.spectrebowling.com/shelves/spectre-cloud-documentation",
  glossary:
    "https://wiki.spectrebowling.com/books/09-tips-troubleshooting-reference/page/936-glossary-of-all-terms-and-abbreviations-used-in-spectre-cloud",
  gettingStarted: "https://wiki.spectrebowling.com/books/01-getting-started",
  tabletAtDrillPress:
    "https://wiki.spectrebowling.com/books/08-account-business/page/823-using-spectre-cloud-on-a-tablet-at-the-drill-press",
  drillPressCalibration:
    "https://wiki.spectrebowling.com/books/02-settings/chapter/24-drill-press-calibration",
  autoSuggestions:
    "https://wiki.spectrebowling.com/books/02-settings/page/261-overview-why-leaving-all-auto-suggestions-on-saves-time",
  cutToCut:
    "https://wiki.spectrebowling.com/books/02-settings/page/269-autofill-cut-to-cut-measurement-based-on-insert-typesize",
  fiveSixteen:
    "https://wiki.spectrebowling.com/books/02-settings/page/268-auto-calculate-ring-finger-span-based-on-516-rule",
  thumbPreferences:
    "https://wiki.spectrebowling.com/books/02-settings/chapter/23-thumb-hole-preferences",
  ovalCalculator: "https://wiki.spectrebowling.com/books/05-oval-calculator",
  communityTraining:
    "https://wiki.spectrebowling.com/books/09-tips-troubleshooting-reference/page/943-community-resources-and-pro-shop-training-shelf",
  layoutPreferences:
    "https://wiki.spectrebowling.com/books/02-settings/chapter/25-layout-preferences",
  introVideo: "https://youtu.be/nIiqe2jVuyw",
};

const PAGE = {
  defaults: {
    eyebrow: "settingsPage.eyebrow.technicalPreferences",
    title: "settingsPage.title.defaults",
  },
  "auto-suggestions": {
    eyebrow: "settingsPage.eyebrow.automation",
    title: "settingsPage.title.autoSuggestions",
  },
  "inserts-ovals": {
    eyebrow: "settingsPage.eyebrow.thumbInsertBehavior",
    title: "settingsPage.title.insertsOvals",
  },
  "drill-press": {
    eyebrow: "settingsPage.eyebrow.machineSetup",
    title: "settingsPage.title.drillPress",
  },
  layout: {
    eyebrow: "settingsPage.eyebrow.arsenal",
    title: "settingsPage.title.layout",
  },
  help: {
    eyebrow: "settingsPage.eyebrow.support",
    title: "settingsPage.title.help",
  },
  "job-board": {
    eyebrow: "settingsPage.eyebrow.plugin",
    title: "settingsPage.title.jobBoard",
  },
  "bowler-plus": {
    eyebrow: "settingsPage.eyebrow.plugin",
    title: "settingsPage.title.bowlerPlus",
  },
  "arsenal-plus": {
    eyebrow: "settingsPage.eyebrow.plugin",
    title: "settingsPage.title.arsenalPlus",
  },
  pos: {
    eyebrow: "settingsPage.eyebrow.plugin",
    title: "settingsPage.title.pos",
  },
  referrals: {
    eyebrow: "settingsPage.eyebrow.growth",
    title: "settingsPage.title.referrals",
  },
};

const languages = [
  { value: "en", label: "English" },
  { value: "fr", label: "French" },
  { value: "es", label: "Spanish" },
  { value: "de", label: "Deutsch" },
  { value: "it", label: "Italian" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "pt", label: "Portuguese" },
  { value: "zh", label: "Chinese" },
];

const MenuButton = styled.button`
  width: 100%;
  border: 1px solid ${(props) => (props.$active ? "#00187e" : "#d8deea")};
  border-radius: 16px;
  padding: 13px 16px;
  text-align: left;
  font: 600 14px/1.2 Poppins, sans-serif;
  color: ${(props) => (props.$active ? "#ffffff" : "#1f2a44")};
  background: ${(props) => (props.$active ? "#00187e" : "#ffffff")};
  box-shadow: none;
  transition: border-color 0.18s ease, background 0.18s ease,
    color 0.18s ease;

  &:hover {
    border-color: #00187e;
    background: ${(props) => (props.$active ? "#00187e" : "#f7f9fd")};
  }

  @media only screen and (max-width: 991px) {
    width: auto;
    flex: 0 0 auto;
    border-radius: 999px;
    white-space: nowrap;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    rgba(0, 24, 126, 0) 0%,
    rgba(0, 24, 126, 0.18) 18%,
    rgba(0, 24, 126, 0.18) 82%,
    rgba(0, 24, 126, 0) 100%
  );
  margin: 6px 0;

  @media only screen and (max-width: 991px) {
    width: 1px;
    height: 28px;
    margin: 0 2px;
    flex: 0 0 auto;
    background: linear-gradient(
      180deg,
      rgba(0, 24, 126, 0) 0%,
      rgba(0, 24, 126, 0.18) 18%,
      rgba(0, 24, 126, 0.18) 82%,
      rgba(0, 24, 126, 0) 100%
    );
  }
`;

const PageWrap = styled.div`
  --paper: #fffdf8;
  --panel: rgba(255, 255, 255, 0.88);
  --line: rgba(12, 31, 83, 0.1);
  --ink: #18233b;
  --muted: #5b6781;
  width: 100vw;
  min-height: 100%;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  padding: 0 24px 0 0;
  box-sizing: border-box;
  background: #eef1f4;

  @media only screen and (max-width: 991px) {
    padding: 20px 16px 88px;
  }
`;

const Shell = styled.div`
  width: 100%;
  padding-left: 304px;
  padding-top: 28px;
  padding-bottom: 120px;
  box-sizing: border-box;
  background: #ffffff;
  min-height: 100vh;

  @media only screen and (max-width: 991px) {
    padding-left: 0;
    padding-top: 0;
    padding-bottom: 0;
    background: transparent;
    min-height: auto;
  }
`;

const Sidebar = styled.aside`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 280px;
  border-radius: 0;
  padding: 88px 22px 24px;
  box-sizing: border-box;
  overflow-y: auto;
  background: linear-gradient(180deg, #f1f3f6 0%, #e7ebef 100%);
  box-shadow:
    inset -1px 0 0 rgba(12, 31, 83, 0.06);
  backdrop-filter: blur(16px);
  z-index: 3;

  @media only screen and (max-width: 991px) {
    position: static;
    width: auto;
    border-radius: 0;
    padding: 16px 0 10px;
    background: transparent;
    box-shadow: none;
    backdrop-filter: none;
    overflow: visible;
  }
`;

const SidebarTitle = styled.div`
  font: 700 11px/1.2 Montserrat, sans-serif;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #50607f;
  margin-bottom: 16px;

  @media only screen and (max-width: 991px) {
    padding: 0 16px;
    margin-bottom: 12px;
  }
`;

const SidebarList = styled.div`
  display: grid;
  gap: 10px;

  @media only screen and (max-width: 991px) {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0 16px 6px;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Content = styled.main`
  display: grid;
  gap: 18px;
  align-content: start;
  width: calc(100vw - 304px);
  min-height: calc(100vh - 56px);
  padding: 22px 24px 32px;
  box-sizing: border-box;

  & > * {
    width: min(960px, 100%);
    margin: 0 auto;
    box-sizing: border-box;
  }

  @media only screen and (max-width: 991px) {
    width: 100%;
    padding: 0;
    background: transparent;
    min-height: auto;

    & > * {
      width: 100%;
    }
  }
`;

const Hero = styled.section`
  border-radius: 32px;
  padding: 28px;
  background: #ffffff;
  box-shadow: 0 24px 56px rgba(8, 29, 75, 0.09);
`;

const Eyebrow = styled.div`
  font: 700 11px/1.2 Montserrat, sans-serif;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #576987;
  margin-bottom: 10px;
`;

const HeroTitle = styled.h1`
  margin: 0;
  color: #0e1d3d;
  font: 700 34px/1.08 Lato, sans-serif;
`;

const Card = styled.section`
  border-radius: 28px;
  padding: 24px;
  background: var(--panel);
  box-shadow:
    0 20px 44px rgba(8, 29, 75, 0.08),
    inset 0 0 0 1px rgba(255, 255, 255, 0.75);
`;

const CardTitle = styled.h2`
  margin: 0;
  color: var(--ink);
  font: 700 22px/1.15 Lato, sans-serif;
`;

const CardSubtitle = styled.p`
  margin: 4px 0 0 0;
  color: #6b7890;
  font: 500 13px/1.45 Poppins, sans-serif;
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
`;

const ExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #00187e;
  font: 700 13px/1.3 Poppins, sans-serif;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const GroupGrid = styled.div`
  display: grid;
  gap: 18px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  ${(props) =>
    props.$compact &&
    css`
      grid-template-columns: repeat(3, minmax(0, 1fr));
    `}

  @media only screen and (max-width: 1150px) {
    grid-template-columns: 1fr;
  }
`;

const FieldCard = styled.div`
  border-radius: 22px;
  padding: 18px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid var(--line);
  min-height: 148px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  ${(props) =>
    props.$compact &&
    css`
      min-height: 0;
    `}
`;

const FieldLabel = styled.div`
  color: #172544;
  font: 700 15px/1.4 Poppins, sans-serif;
`;

const FieldHint = styled.div`
  color: #68758d;
  font: 400 13px/1.5 Poppins, sans-serif;
  min-height: 40px;

  ${(props) =>
    props.$compact &&
    css`
      min-height: 0;
    `}
`;

const ChoiceRow = styled.div`
  display: inline-flex;
  width: fit-content;
  border-radius: 16px;
  padding: 4px;
  background: #edf2fc;
  gap: 4px;
  flex-wrap: wrap;

  /* On phones, selectors with 4+ options wrap unevenly (3 + a lonely one).
     Let the buttons share the full width and shrink so they stay on ONE row. */
  @media only screen and (max-width: 640px) {
    ${(props) =>
      props.$fill &&
      css`
        display: flex;
        flex-wrap: nowrap;
        width: 100%;

        & > button {
          flex: 1 1 0;
          min-width: 0;
          padding-left: 6px;
          padding-right: 6px;
          font-size: 11px;
        }
      `}
  }
`;

const ChoiceButton = styled.button`
  border: 1px solid ${(props) => (props.$active ? "#00187e" : "transparent")};
  min-width: 64px;
  padding: 10px 14px;
  border-radius: 12px;
  background: ${(props) => (props.$active ? "#00187e" : "transparent")};
  color: ${(props) => (props.$active ? "#ffffff" : "#32415f")};
  font: 700 12px/1.2 Poppins, sans-serif;
  letter-spacing: 0.02em;
  box-shadow: none;
  transition: border-color 0.18s ease, background 0.18s ease,
    color 0.18s ease;

  &:hover {
    border-color: #00187e;
    background: ${(props) => (props.$active ? "#00187e" : "#ffffff")};
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: #142240;
  font: 700 18px/1.2 Lato, sans-serif;
`;

const SectionHint = styled.p`
  margin: 8px 0 0;
  color: #6b7890;
  font: 400 13px/1.55 Poppins, sans-serif;
`;

const SectionBlock = styled.div`
  display: grid;
  gap: 14px;
`;

const InsertWrap = styled.div`
  display: grid;
  gap: 12px;
`;

const InsertCard = styled.div`
  border-radius: 22px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.92);
  overflow: hidden;
`;

const InsertHead = styled.button`
  width: 100%;
  border: 0;
  background: transparent;
  padding: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  color: #162541;
  font: 700 15px/1.25 Poppins, sans-serif;
`;

const InsertSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #00187e;
`;

const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(0, 24, 126, 0.08);
  color: #00187e;
  font: 700 12px/1 Poppins, sans-serif;
`;

const InsertBody = styled.div`
  padding: 0 18px 18px;
  display: grid;
  gap: 10px;
`;

const InsertItem = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 14px;
  padding: 10px 12px;
  background: #f7f9fd;
  color: #2c3955;
  font: 500 13px/1.3 Poppins, sans-serif;
`;

const SelectWrap = styled.div`
  max-width: 320px;

  .ant-select {
    width: 100%;
  }

  .ant-select-selector {
    min-height: 48px !important;
    border-radius: 14px !important;
    border-color: rgba(0, 24, 126, 0.16) !important;
    padding-top: 8px !important;
  }
`;

const LinkGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media only screen and (max-width: 1150px) {
    grid-template-columns: 1fr;
  }
`;

const LinkCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px;
  border-radius: 22px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.92);
  text-decoration: none;
`;

const LinkLabel = styled.span`
  color: #142240;
  font: 700 15px/1.35 Poppins, sans-serif;
`;

const LinkMeta = styled.span`
  color: #6b7890;
  font: 400 13px/1.55 Poppins, sans-serif;
`;

const LinkTag = styled.span`
  color: #00187e;
  font: 700 12px/1.2 Poppins, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const MetricInput = styled(DebounceInput)`
  width: 100%;
  max-width: 180px;
  min-height: 48px;
  border-radius: 14px;
  border: 1px solid rgba(0, 24, 126, 0.16);
  padding: 12px 14px;
  background: #ffffff;
  color: #18233b;
  font: 600 16px/1.2 Poppins, sans-serif;
`;

const ActionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ActionButton = styled.button`
  border: 1px solid ${(props) => (props.$secondary ? "#d8deea" : "#00187e")};
  border-radius: 15px;
  padding: 12px 16px;
  background: ${(props) => (props.$secondary ? "#ffffff" : "#00187e")};
  color: ${(props) => (props.$secondary ? "#00187e" : "#ffffff")};
  font: 700 13px/1.2 Poppins, sans-serif;
  box-shadow: none;
`;

const ListTable = styled.div`
  display: grid;
  gap: 10px;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: ${(props) => props.$template};
  gap: 12px;
  padding: 0 6px;
  color: #6b7890;
  font: 700 11px/1.2 Poppins, sans-serif;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media only screen and (max-width: 900px) {
    display: none;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: ${(props) => props.$template};
  gap: 12px;
  align-items: center;
  padding: 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(12, 31, 83, 0.08);

  @media only screen and (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const RowValue = styled.div`
  color: #1d2b48;
  font: 500 13px/1.45 Poppins, sans-serif;
  word-break: break-word;
`;

const RowActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  @media only screen and (max-width: 900px) {
    justify-content: flex-start;
  }
`;

const SmallAction = styled.button`
  border: 1px solid #d8deea;
  border-radius: 12px;
  padding: 9px 12px;
  background: #ffffff;
  color: #00187e;
  font: 700 12px/1.2 Poppins, sans-serif;
  box-shadow: none;

  ${(props) =>
    props.$danger &&
    css`
      border-color: #e1bcbc;
      background: #ffffff;
      color: #b53434;
    `}
`;

const ReferralCode = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 180px;
  padding: 18px 22px;
  border-radius: 20px;
  background: linear-gradient(135deg, #00187e 0%, #0f4ec9 100%);
  color: #ffffff;
  font: 700 28px/1 Lato, sans-serif;
  letter-spacing: 0.12em;
`;

const getParsedValue = (value) => {
  if (value === "true") return true;
  if (value === "false") return false;
  return value;
};

const ChoiceField = ({
  label,
  hint,
  value,
  choices,
  onSelect,
  link,
  compact,
}) => {
  // 2-3 options already fit one phone row; 4+ need to shrink to stay on one line.
  const fillRow = choices.length >= 4;
  return (
    <FieldCard $compact={compact}>
      <FieldLabel>{label}</FieldLabel>
      <FieldHint $compact={compact}>{hint}</FieldHint>
      <ChoiceRow $fill={fillRow}>
        {choices.map((choice) => (
          <ChoiceButton
            key={choice.value}
            type="button"
            $active={value === choice.value}
            onClick={() => onSelect(choice.value)}
          >
            {choice.label}
          </ChoiceButton>
        ))}
      </ChoiceRow>
      {link && (
        <ExternalLink href={link.href} target="_blank" rel="noreferrer">
          {link.label} →
        </ExternalLink>
      )}
    </FieldCard>
  );
};

const SettingsRedux = ({
  initialValues,
  initialLanguage,
  settingsHandleForm,
  settingsChangeLanguage,
  pluginSubscriptions,
  plugin_settings_job_board_include_price,
  plugin_settings_job_board_currency,
  plugin_settings_job_board_timer,
  plugin_settings_convert_layouts,
  plugin_settings_require_signature,
  plugin_settings_require_serial_number,
  plugin_settings_pos_send_from_spectre,
  plugin_settings_pos_jobs_from_sales,
  provisionBackend,
  plugin_settings,
  jobServices,
  salesPersons,
  locations,
  updateSharedSettings,
  setShowJobServiceModal,
  setShowSalespersonModal,
  setShowSalespersonEditModal,
  setShowLocationModal,
  setShowLocationEditModal,
  setShowJobEditServiceModal,
  deleteJobService,
  deleteSalesPerson,
  deleteLocation,
  updateJobServices,
  language,
  updating,
  provisionStatus,
}) => {
  const [t, i18n] = useTranslation();
  const values = initialValues || {};
  const [activePage, setActivePage] = useState("defaults");
  const [expandedInsertGroups, setExpandedInsertGroups] = useState({});
  const [movedServices, setMovedServices] = useState(jobServices);
  const [isReordering, setIsReordering] = useState(false);
  const [haveReordered, setHaveReordered] = useState(false);
  const [movedSalesPersons, setMovedSalesPersons] = useState(salesPersons);
  const [movedLocations, setMovedLocations] = useState(locations);
  // #854: the pro shop's list of home centers, typed once here so client
  // creation picks from it instead of retyping the same center every time.
  const [newBowlingCenter, setNewBowlingCenter] = useState("");

  // When switching sections, reset the scroll position so the new section
  // starts at its title instead of inheriting the previous page's scroll (#821).
  const contentRef = useRef(null);
  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;
    let el = node.parentElement;
    while (el) {
      const overflowY = window.getComputedStyle(el).overflowY;
      if (
        (overflowY === "auto" || overflowY === "scroll") &&
        el.scrollHeight > el.clientHeight
      ) {
        el.scrollTop = 0;
        return;
      }
      el = el.parentElement;
    }
    window.scrollTo(0, 0);
  }, [activePage]);

  const setAppValue = (path, rawValue) => {
    settingsHandleForm({
      appSettings: R.assocPath(path, getParsedValue(rawValue), values),
    });
  };

  const bowlingCenters = values.bowlerPreferences?.bowling_centers || [];

  const addBowlingCenter = () => {
    const name = newBowlingCenter.trim();
    // Case-insensitive, because "Salon Buffa" and "salon buffa" are the same
    // center and two of them in the list help nobody.
    const exists = bowlingCenters.some(
      (center) => center.toLowerCase() === name.toLowerCase()
    );
    if (!name || exists) {
      setNewBowlingCenter("");
      return;
    }
    setAppValue(
      ["bowlerPreferences", "bowling_centers"],
      [...bowlingCenters, name].sort((a, b) => a.localeCompare(b))
    );
    setNewBowlingCenter("");
  };

  const removeBowlingCenter = (name) =>
    setAppValue(
      ["bowlerPreferences", "bowling_centers"],
      bowlingCenters.filter((center) => center !== name)
    );

  const handleLanguage = (language) => {
    i18n.changeLanguage(language);
    settingsChangeLanguage({ language });
  };

  const moveService = useCallback(
    (dragIndex, hoverIndex) => {
      const dragItem = movedServices[dragIndex];
      const hoverItem = movedServices[hoverIndex];

      setMovedServices((services) => {
        const updated = [...services];
        updated[dragIndex] = hoverItem;
        updated[hoverIndex] = dragItem;
        return updated;
      });
    },
    [movedServices]
  );

  useEffect(() => {
    setMovedServices(jobServices);
  }, [jobServices]);

  useEffect(() => {
    setMovedSalesPersons(salesPersons);
  }, [salesPersons]);

  useEffect(() => {
    setMovedLocations(locations);
  }, [locations]);

  useEffect(() => {
    if (!updating && isReordering && haveReordered) {
      setIsReordering(false);
      setHaveReordered(false);
    }
  }, [updating, isReordering, haveReordered]);

  const toggleInsertGroup = (groupName) => {
    setExpandedInsertGroups((current) => ({
      ...current,
      [groupName]: !current[groupName],
    }));
  };

  const updateInsertGroup = (groupName, nextItems) => {
    settingsHandleForm({
      appSettings: {
        ...values,
        insertOptions: {
          ...values.insertOptions,
          [groupName]: nextItems,
        },
      },
    });
  };

  const setWholeInsertGroup = (groupName, checked) => {
    const items = (values.insertOptions?.[groupName] || []).map((item) => ({
      ...item,
      use: checked,
    }));
    updateInsertGroup(groupName, items);
  };

  const setInsertItem = (groupName, itemName, checked) => {
    const items = (values.insertOptions?.[groupName] || []).map((item) =>
      item.name === itemName ? { ...item, use: checked } : item
    );
    updateInsertGroup(groupName, items);
  };

  const insertGroups = useMemo(
    () =>
      Object.entries(values.insertOptions || {}).sort(([left], [right]) =>
        left.localeCompare(right)
      ),
    [values.insertOptions]
  );

  const content = PAGE[activePage];
  const {
    systemDefault = {},
    autoSuggestions = {},
    thumbHolePreferences = {},
    drillPressCalibration = {},
    layoutPreferences = {},
    bowlerPreferences = {},
  } = values;

  const hasJobBoard = pluginSubscriptions.indexOf("JOB_BOARD") >= 0;
  const hasBowlerPlus = pluginSubscriptions.indexOf("BOWLER_PLUS") >= 0;
  const hasArsenalPlus =
    pluginSubscriptions.indexOf("BOWLING_DATABASE") >= 0;
  const hasPos =
    pluginSubscriptions.indexOf("SPECTRE_POS") >= 0 ||
    (provisionStatus &&
      provisionStatus !== "ready" &&
      provisionStatus !== "error");

  const menuItems = useMemo(() => {
    const items = [
      { id: "defaults", label: t("settingsPage.title.defaults") },
      { id: "auto-suggestions", label: t("settingsPage.title.autoSuggestions") },
      { id: "inserts-ovals", label: t("settingsPage.title.insertsOvals") },
      { id: "drill-press", label: t("settingsPage.title.drillPress") },
      { id: "layout", label: t("settingsPage.title.layout") },
    ];

    if (hasJobBoard || hasBowlerPlus || hasArsenalPlus || hasPos) {
      items.push({ id: "divider", type: "divider" });
    }
    if (hasJobBoard)
      items.push({ id: "job-board", label: t("settingsPage.title.jobBoard") });
    if (hasBowlerPlus)
      items.push({
        id: "bowler-plus",
        label: t("settingsPage.title.bowlerPlus"),
      });
    if (hasArsenalPlus)
      items.push({
        id: "arsenal-plus",
        label: t("settingsPage.title.arsenalPlus"),
      });
    if (hasPos) items.push({ id: "pos", label: t("settingsPage.title.pos") });

    items.push({ id: "divider-secondary", type: "divider" });
    items.push({ id: "help", label: t("settingsPage.title.help") });
    items.push({ id: "referrals", label: t("settingsPage.title.referrals") });

    return items;
  }, [hasArsenalPlus, hasBowlerPlus, hasJobBoard, hasPos, t]);

  useEffect(() => {
    const validPages = menuItems.filter((item) => item.type !== "divider");
    if (!validPages.some((item) => item.id === activePage)) {
      setActivePage(validPages[0]?.id || "defaults");
    }
  }, [activePage, menuItems]);

  const updatePluginSetting = (key, value) => {
    updateSharedSettings({
      shared_settings: {
        appSettings: values,
        language,
        plugin_settings: {
          ...plugin_settings,
          [key]: value,
        },
      },
    });
  };

  const handleReordering = () => {
    movedServices.forEach((service, idx) => {
      if (service.sort_order !== idx + 1) {
        service.sort_order = idx + 1;
      }
    });
    updateJobServices({ jobServices: movedServices });
    setHaveReordered(true);
  };

  const helpCards = [
    {
      tag: "Video",
      label: "Spectre Cloud introduction",
      meta: "",
      href: PAGE_LINKS.introVideo,
    },
    {
      tag: "Wiki",
      label: "Getting started",
      meta: "",
      href: PAGE_LINKS.gettingStarted,
    },
    {
      tag: "Guide",
      label: "Tether tablet to your drill press",
      meta: "",
      href: PAGE_LINKS.tabletAtDrillPress,
    },
    {
      tag: "Guide",
      label: "Drill press calibration",
      meta: "",
      href: PAGE_LINKS.drillPressCalibration,
    },
    {
      tag: "Guide",
      label: "Oval calculation",
      meta: "",
      href: PAGE_LINKS.ovalCalculator,
    },
    {
      tag: "Wiki",
      label: "Spectre Cloud user manual",
      meta: "",
      href: PAGE_LINKS.manual,
    },
    {
      tag: "Learning",
      label: "e-Learning",
      meta:
        "Sign up today to learn the tricks of the trade with our staff of certified professionals.",
      href: PAGE_LINKS.communityTraining,
    },
  ];

  return (
    <DndProvider backend={HTML5Backend}>
      <PageWrap>
      <Shell>
        <Sidebar>
          <SidebarTitle>{t("settingsPage.menuTitle")}</SidebarTitle>
          <SidebarList>
            {menuItems.map((item) =>
              item.type === "divider" ? (
                <MenuDivider key={item.id} />
              ) : (
                <MenuButton
                  key={item.id}
                  type="button"
                  $active={activePage === item.id}
                  onClick={() => setActivePage(item.id)}
                >
                  {item.label}
                </MenuButton>
              )
            )}
          </SidebarList>
        </Sidebar>

        <Content ref={contentRef}>
          <Hero>
            <Eyebrow>{t(content.eyebrow)}</Eyebrow>
            <HeroTitle>{t(content.title)}</HeroTitle>
          </Hero>

          {activePage === "defaults" && (
            <>
              <Card>
                <CardHead>
                  <div>
                    <CardTitle>{t("settingsPage.card.language")}</CardTitle>
                  </div>
                </CardHead>
                <SelectWrap>
                  <Select
                    value={initialLanguage}
                    options={languages}
                    onChange={handleLanguage}
                  />
                </SelectWrap>
              </Card>

              <Card>
                <CardHead>
                  <div>
                    <CardTitle>{t("settingsPage.card.bowlerDefaults")}</CardTitle>
                  </div>
                </CardHead>
                <FieldGrid>
                  <ChoiceField
                    label={t("settingsPage.field.defaultHand.label")}
                    hint={t("settingsPage.field.defaultHand.hint")}
                    value={systemDefault.default_hand}
                    onSelect={(value) =>
                      setAppValue(["systemDefault", "default_hand"], value)
                    }
                    choices={[
                      { label: t("settingsPage.choice.lh"), value: "left_hander" },
                      { label: t("settingsPage.choice.rh"), value: "right_hander" },
                    ]}
                  />
                  <ChoiceField
                    label={t("settingsPage.field.gripType.label")}
                    hint={t("settingsPage.field.gripType.hint")}
                    value={systemDefault.default_ball_grip}
                    onSelect={(value) =>
                      setAppValue(
                        ["systemDefault", "default_ball_grip"],
                        value
                      )
                    }
                    choices={[
                      { label: t("settingsPage.choice.conventional"), value: "conventional" },
                      { label: t("settingsPage.choice.fingerTip"), value: "finger_tip" },
                    ]}
                  />
                  <ChoiceField
                    label={t("settingsPage.field.mandatoryEmail.label")}
                    hint={t("settingsPage.field.mandatoryEmail.hint")}
                    value={bowlerPreferences.require_email}
                    onSelect={(value) =>
                      setAppValue(["bowlerPreferences", "require_email"], value)
                    }
                    choices={[
                      { label: t("settingsPage.common.no"), value: false },
                      { label: t("settingsPage.common.yes"), value: true },
                    ]}
                  />
                  <ChoiceField
                    label={t("settingsPage.field.mandatoryPhone.label")}
                    hint={t("settingsPage.field.mandatoryPhone.hint")}
                    value={bowlerPreferences.require_phone}
                    onSelect={(value) =>
                      setAppValue(["bowlerPreferences", "require_phone"], value)
                    }
                    choices={[
                      { label: t("settingsPage.common.no"), value: false },
                      { label: t("settingsPage.common.yes"), value: true },
                    ]}
                  />
                </FieldGrid>
              </Card>

              <Card>
                <CardHead>
                  <div>
                    <CardTitle>{t("settingsPage.card.specSheetDefaults")}</CardTitle>
                  </div>
                </CardHead>
                <FieldGrid>
                  <ChoiceField
                    label={t("settingsPage.field.pitchesDisplayed.label")}
                    hint={t("settingsPage.field.pitchesDisplayed.hint")}
                    value={systemDefault.display_measurement}
                    onSelect={(value) =>
                      setAppValue(
                        ["systemDefault", "display_measurement"],
                        value
                      )
                    }
                    choices={[
                      { label: t("settingsPage.choice.decimal"), value: "decimal" },
                      { label: t("settingsPage.choice.fraction"), value: "fraction" },
                    ]}
                  />
                  <ChoiceField
                    label={t("settingsPage.field.dropdownMeasurements.label")}
                    hint={t("settingsPage.field.dropdownMeasurements.hint")}
                    value={systemDefault.span_measurement}
                    onSelect={(value) =>
                      setAppValue(["systemDefault", "span_measurement"], value)
                    }
                    choices={[
                      { label: "16", value: "16" },
                      { label: "16+", value: "16+" },
                      { label: "32", value: "32" },
                      { label: "64", value: "64" },
                    ]}
                  />
                  <ChoiceField
                    label={t("settingsPage.field.dropdownPitches.label")}
                    hint={t("settingsPage.field.dropdownPitches.hint")}
                    value={systemDefault.pitch_measurement}
                    onSelect={(value) =>
                      setAppValue(["systemDefault", "pitch_measurement"], value)
                    }
                    choices={[
                      { label: "16", value: "16" },
                      { label: "16+", value: "16+" },
                      { label: "32", value: "32" },
                      { label: "64", value: "64" },
                    ]}
                  />
                  <ChoiceField
                    label={t("settingsPage.field.displayPolarity.label")}
                    hint={t("settingsPage.field.displayPolarity.hint")}
                    value={drillPressCalibration.oval_machine_show_pitch_plus_minus}
                    onSelect={(value) =>
                      setAppValue(
                        [
                          "drillPressCalibration",
                          "oval_machine_show_pitch_plus_minus",
                        ],
                        value
                      )
                    }
                    choices={[
                      { label: t("settingsPage.common.no"), value: false },
                      { label: t("settingsPage.common.yes"), value: true },
                    ]}
                  />
                  <ChoiceField
                    label={t("settingsPage.field.defaultInsertInstall.label")}
                    hint={t("settingsPage.field.defaultInsertInstall.hint")}
                    value={systemDefault.default_vacu}
                    onSelect={(value) =>
                      setAppValue(["systemDefault", "default_vacu"], value)
                    }
                    choices={[
                      { label: t("settingsPage.choice.std"), value: "std" },
                      { label: t("settingsPage.choice.vacu"), value: "vacu" },
                    ]}
                  />
                  <ChoiceField
                    label={t("settingsPage.field.vacuIncrement.label")}
                    hint={t("settingsPage.field.vacuIncrement.hint")}
                    value={systemDefault.default_vacu_increment}
                    onSelect={(value) =>
                      setAppValue(
                        ["systemDefault", "default_vacu_increment"],
                        value
                      )
                    }
                    choices={[
                      { label: "1/16", value: "1/16" },
                      { label: "3/64", value: "3/64" },
                      { label: "1/32", value: "1/32" },
                      { label: "1/64", value: "1/64" },
                    ]}
                  />
                </FieldGrid>
              </Card>

              <Card>
                <CardHead>
                  <div>
                    <CardTitle>{t("settingsPage.card.thumbDefaults")}</CardTitle>
                  </div>
                </CardHead>
                <FieldGrid>
                  <ChoiceField
                    label={t("settingsPage.field.defaultHoleType.label")}
                    hint={t("settingsPage.field.defaultHoleType.hint")}
                    value={thumbHolePreferences.default_thm_type}
                    onSelect={(value) =>
                      setAppValue(
                        ["thumbHolePreferences", "default_thm_type"],
                        value
                      )
                    }
                    choices={[
                      { label: t("settingsPage.choice.round"), value: "Round" },
                      { label: t("settingsPage.choice.oval"), value: "Oval" },
                    ]}
                  />
                  <ChoiceField
                    label={t("settingsPage.field.ovalDegreeIncrements.label")}
                    hint={t("settingsPage.field.ovalDegreeIncrements.hint")}
                    value={systemDefault.angle_measurement}
                    onSelect={(value) =>
                      setAppValue(["systemDefault", "angle_measurement"], value)
                    }
                    choices={[
                      { label: "1°", value: "1" },
                      { label: "5°", value: "5" },
                    ]}
                  />
                </FieldGrid>
              </Card>
            </>
          )}

          {activePage === "auto-suggestions" && (
            <Card>
              <CardHead style={{ justifyContent: "flex-end", marginBottom: 0 }}>
                <ExternalLink
                  href={PAGE_LINKS.autoSuggestions}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("settingsPage.common.openWiki")} →
                </ExternalLink>
              </CardHead>

              <GroupGrid>
                <SectionBlock>
                  <div>
                    <SectionTitle>{t("settingsPage.section.pitches")}</SectionTitle>
                    <SectionHint>
                      Suggestions driven by flexibility, CLT, and lateral
                      population rules.
                    </SectionHint>
                  </div>
                  <FieldGrid>
                    <ChoiceField
                      label={t("settingsPage.field.thumbPitchSuggestion.label")}
                      hint={t("settingsPage.field.thumbPitchSuggestion.hint")}
                      value={autoSuggestions.spec_sheet_auto_thumb_pitches}
                      onSelect={(value) =>
                        setAppValue(
                          ["autoSuggestions", "spec_sheet_auto_thumb_pitches"],
                          value
                        )
                      }
                      choices={[
                        { label: t("settingsPage.common.off"), value: false },
                        { label: t("settingsPage.common.on"), value: true },
                      ]}
                    />
                    <ChoiceField
                      label={t("settingsPage.field.populateLateral.label")}
                      hint={t("settingsPage.field.populateLateral.hint")}
                      value={autoSuggestions.spec_sheet_auto_lateral_pitches}
                      onSelect={(value) =>
                        setAppValue(
                          [
                            "autoSuggestions",
                            "spec_sheet_auto_lateral_pitches",
                          ],
                          value
                        )
                      }
                      choices={[
                        { label: t("settingsPage.common.off"), value: false },
                        { label: t("settingsPage.common.on"), value: true },
                      ]}
                    />
                    <ChoiceField
                      label={t("settingsPage.field.pitchesClt.label")}
                      hint={t("settingsPage.field.pitchesClt.hint")}
                      value={autoSuggestions.spec_sheet_auto_lateral_pitches_clt}
                      onSelect={(value) =>
                        setAppValue(
                          [
                            "autoSuggestions",
                            "spec_sheet_auto_lateral_pitches_clt",
                          ],
                          value
                        )
                      }
                      choices={[
                        { label: t("settingsPage.common.off"), value: false },
                        { label: t("settingsPage.common.on"), value: true },
                      ]}
                    />
                  </FieldGrid>
                </SectionBlock>

                <SectionBlock>
                  <div>
                    <SectionTitle>{t("settingsPage.section.measurements")}</SectionTitle>
                    <SectionHint>
                      Rules that shape bridge and cut-to-cut calculations.
                    </SectionHint>
                  </div>
                  <FieldGrid>
                    <ChoiceField
                      label={t("settingsPage.field.autoCutToCut.label")}
                      hint={t("settingsPage.field.autoCutToCut.hint")}
                      value={autoSuggestions.spec_sheet_auto_cut_to_cut}
                      onSelect={(value) =>
                        setAppValue(
                          ["autoSuggestions", "spec_sheet_auto_cut_to_cut"],
                          value
                        )
                      }
                      choices={[
                        { label: t("settingsPage.common.off"), value: false },
                        { label: t("settingsPage.common.on"), value: true },
                      ]}
                      link={{
                        href: PAGE_LINKS.cutToCut,
                        label: t("settingsPage.common.wikiReference"),
                      }}
                    />
                    <ChoiceField
                      label={t("settingsPage.field.autoBridge.label")}
                      hint={t("settingsPage.field.autoBridge.hint")}
                      value={autoSuggestions.spec_sheet_auto_fill_bridge}
                      onSelect={(value) =>
                        setAppValue(
                          ["autoSuggestions", "spec_sheet_auto_fill_bridge"],
                          value
                        )
                      }
                      choices={[
                        { label: t("settingsPage.common.off"), value: false },
                        { label: t("settingsPage.common.on"), value: true },
                      ]}
                    />
                    <ChoiceField
                      label={t("settingsPage.field.ringFinger516.label")}
                      hint={t("settingsPage.field.ringFinger516.hint")}
                      value={systemDefault.display_f2_span_full_difference}
                      onSelect={(value) =>
                        setAppValue(
                          [
                            "systemDefault",
                            "display_f2_span_full_difference",
                          ],
                          value
                        )
                      }
                      choices={[
                        { label: t("settingsPage.common.off"), value: false },
                        { label: t("settingsPage.common.on"), value: true },
                      ]}
                      link={{
                        href: PAGE_LINKS.fiveSixteen,
                        label: t("settingsPage.common.wikiReference"),
                      }}
                    />
                  </FieldGrid>
                </SectionBlock>

                <SectionBlock>
                  <div>
                    <SectionTitle>{t("settingsPage.section.fingers")}</SectionTitle>
                    <SectionHint>
                      Controls for defaulting insert outer diameters and matching
                      finger insert selections.
                    </SectionHint>
                  </div>
                  <FieldGrid>
                    <ChoiceField
                      label={t("settingsPage.field.autoInsertOd.label")}
                      hint={t("settingsPage.field.autoInsertOd.hint")}
                      value={autoSuggestions.spec_sheet_auto_insert_od}
                      onSelect={(value) =>
                        setAppValue(
                          ["autoSuggestions", "spec_sheet_auto_insert_od"],
                          value
                        )
                      }
                      choices={[
                        { label: t("settingsPage.common.off"), value: false },
                        { label: t("settingsPage.common.on"), value: true },
                      ]}
                    />
                    <ChoiceField
                      label={t("settingsPage.field.dupInsertType.label")}
                      hint={t("settingsPage.field.dupInsertType.hint")}
                      value={autoSuggestions.spec_sheet_auto_repeat_insert_type}
                      onSelect={(value) =>
                        setAppValue(
                          [
                            "autoSuggestions",
                            "spec_sheet_auto_repeat_insert_type",
                          ],
                          value
                        )
                      }
                      choices={[
                        { label: t("settingsPage.common.off"), value: false },
                        { label: t("settingsPage.common.on"), value: true },
                      ]}
                    />
                    <ChoiceField
                      label={t("settingsPage.field.dupInsertSize.label")}
                      hint={t("settingsPage.field.dupInsertSize.hint")}
                      value={autoSuggestions.spec_sheet_auto_repeat_insert_size}
                      onSelect={(value) =>
                        setAppValue(
                          [
                            "autoSuggestions",
                            "spec_sheet_auto_repeat_insert_size",
                          ],
                          value
                        )
                      }
                      choices={[
                        { label: t("settingsPage.common.off"), value: false },
                        { label: t("settingsPage.common.on"), value: true },
                      ]}
                    />
                  </FieldGrid>
                </SectionBlock>

                <SectionBlock>
                  <div>
                    <SectionTitle>{t("settingsPage.section.layouts")}</SectionTitle>
                    <SectionHint>
                      Layout helpers shown during arsenal work.
                    </SectionHint>
                  </div>
                  <FieldGrid>
                    <ChoiceField
                      label={t("settingsPage.field.showSuggestedLayouts.label")}
                      hint={t("settingsPage.field.showSuggestedLayouts.hint")}
                      value={autoSuggestions.spec_sheet_show_suggested_layouts}
                      onSelect={(value) =>
                        setAppValue(
                          [
                            "autoSuggestions",
                            "spec_sheet_show_suggested_layouts",
                          ],
                          value
                        )
                      }
                      choices={[
                        { label: t("settingsPage.common.off"), value: false },
                        { label: t("settingsPage.common.on"), value: true },
                      ]}
                    />
                    {WIZARD_ENABLED && (
                      <ChoiceField
                        label={t(
                          "settingsPage.field.wizardOffer.label",
                          "Offer guided measuring"
                        )}
                        hint={t(
                          "settingsPage.field.wizardOffer.hint",
                          "Offers the measuring wizard on a bowler with no spec sheet yet."
                        )}
                        value={autoSuggestions.spec_sheet_wizard_offer}
                        onSelect={(value) =>
                          setAppValue(
                            ["autoSuggestions", "spec_sheet_wizard_offer"],
                            value
                          )
                        }
                        choices={[
                          { label: t("settingsPage.common.off"), value: false },
                          { label: t("settingsPage.common.on"), value: true },
                        ]}
                      />
                    )}
                  </FieldGrid>
                </SectionBlock>
              </GroupGrid>
            </Card>
          )}

          {activePage === "inserts-ovals" && (
            <>
              <Card>
                <CardHead>
                  <div>
                    <CardTitle>{t("settingsPage.card.ovalThumb")}</CardTitle>
                  </div>
                  <ExternalLink
                    href={PAGE_LINKS.thumbPreferences}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("settingsPage.common.openWiki")} →
                  </ExternalLink>
                </CardHead>

                <FieldGrid>
                  <ChoiceField
                    label={t("settingsPage.field.tableMovement.label")}
                    hint={t("settingsPage.field.tableMovement.hint")}
                    value={thumbHolePreferences.oval_calculation}
                    onSelect={(value) =>
                      setAppValue(
                        ["thumbHolePreferences", "oval_calculation"],
                        value
                      )
                    }
                    choices={[
                      { label: "V/H", value: "trigonometry" },
                      { label: "H", value: "horizontal" },
                      { label: "V", value: "vertical" },
                      { label: t("settingsPage.common.none"), value: "manual" },
                    ]}
                  />
                  <ChoiceField
                    label={t("settingsPage.field.useThumbPitchBase.label")}
                    hint={t("settingsPage.field.useThumbPitchBase.hint")}
                    value={thumbHolePreferences.pitch_added_to_oval_calculation}
                    onSelect={(value) =>
                      setAppValue(
                        [
                          "thumbHolePreferences",
                          "pitch_added_to_oval_calculation",
                        ],
                        value
                      )
                    }
                    choices={[
                      { label: t("settingsPage.common.no"), value: false },
                      { label: t("settingsPage.common.yes"), value: true },
                    ]}
                  />
                  <ChoiceField
                    label={t("settingsPage.field.startingPointOval.label")}
                    hint={t("settingsPage.field.startingPointOval.hint")}
                    value={thumbHolePreferences.pitch_center_edge}
                    onSelect={(value) =>
                      setAppValue(
                        ["thumbHolePreferences", "pitch_center_edge"],
                        value
                      )
                    }
                    choices={[
                      { label: t("settingsPage.choice.topEdge"), value: "edge" },
                      { label: t("settingsPage.choice.center"), value: "center" },
                    ]}
                  />
                  <ChoiceField
                    label={t("settingsPage.field.ovalWidth.label")}
                    hint={t("settingsPage.field.ovalWidth.hint")}
                    value={thumbHolePreferences.oval_width_difference}
                    onSelect={(value) =>
                      setAppValue(
                        ["thumbHolePreferences", "oval_width_difference"],
                        value
                      )
                    }
                    choices={[
                      { label: t("settingsPage.choice.bitSize"), value: "64th" },
                      { label: t("settingsPage.choice.decimal"), value: "decimal" },
                      { label: t("settingsPage.choice.diff"), value: "0" },
                    ]}
                  />
                  <ChoiceField
                    label={t("settingsPage.field.vhDisplay.label")}
                    hint={t("settingsPage.field.vhDisplay.hint")}
                    value={thumbHolePreferences.flip_vh}
                    onSelect={(value) =>
                      setAppValue(["thumbHolePreferences", "flip_vh"], value)
                    }
                    choices={[
                      { label: "V/H", value: "vh" },
                      { label: "H/V", value: "hv" },
                    ]}
                  />
                </FieldGrid>
              </Card>

              <Card>
                <CardHead>
                  <div>
                    <CardTitle>{t("settingsPage.card.insertTypeFilter")}</CardTitle>
                  </div>
                </CardHead>

                <InsertWrap>
                  {insertGroups.map(([groupName, items]) => {
                    const enabledCount = items.filter((item) => item.use).length;
                    const allEnabled =
                      items.length > 0 && enabledCount === items.length;
                    return (
                      <InsertCard key={groupName}>
                        <InsertHead
                          type="button"
                          onClick={() => toggleInsertGroup(groupName)}
                        >
                          <InsertSummary>
                            <Checkbox
                              type="checkbox"
                              checked={allEnabled}
                              onChange={(event) => {
                                event.stopPropagation();
                                setWholeInsertGroup(
                                  groupName,
                                  event.target.checked
                                );
                              }}
                            />
                            <span>{groupName}</span>
                          </InsertSummary>
                          <CountPill>
                            {enabledCount}/{items.length}
                          </CountPill>
                        </InsertHead>
                        {expandedInsertGroups[groupName] && (
                          <InsertBody>
                            {items
                              .slice()
                              .sort((left, right) =>
                                left.name.localeCompare(right.name)
                              )
                              .map((item) => (
                                <InsertItem key={item.name}>
                                  <Checkbox
                                    type="checkbox"
                                    checked={!!item.use}
                                    onChange={(event) =>
                                      setInsertItem(
                                        groupName,
                                        item.name,
                                        event.target.checked
                                      )
                                    }
                                  />
                                  <span>{item.name}</span>
                                </InsertItem>
                              ))}
                          </InsertBody>
                        )}
                      </InsertCard>
                    );
                  })}
                </InsertWrap>
              </Card>
            </>
          )}

          {activePage === "drill-press" && (
            <Card>
              <FieldGrid>
                <ChoiceField
                  compact
                  label={t("settingsPage.field.rightPitchPositive.label")}
                  hint={t("settingsPage.field.rightPitchPositive.hint")}
                  value={drillPressCalibration.oval_machine_right_positive}
                  onSelect={(value) =>
                    setAppValue(
                      [
                        "drillPressCalibration",
                        "oval_machine_right_positive",
                      ],
                      value
                    )
                  }
                  choices={[
                    { label: t("settingsPage.common.no"), value: false },
                    { label: t("settingsPage.common.yes"), value: true },
                  ]}
                />
                <ChoiceField
                  compact
                  label={t("settingsPage.field.forwardThumbPositive.label")}
                  hint={t("settingsPage.field.forwardThumbPositive.hint")}
                  value={
                    drillPressCalibration.oval_machine_forward_thumb_positive
                  }
                  onSelect={(value) =>
                    setAppValue(
                      [
                        "drillPressCalibration",
                        "oval_machine_forward_thumb_positive",
                      ],
                      value
                    )
                  }
                  choices={[
                    { label: t("settingsPage.common.no"), value: false },
                    { label: t("settingsPage.common.yes"), value: true },
                  ]}
                />
              </FieldGrid>
            </Card>
          )}

          {activePage === "layout" && (
            <Card>
              <FieldGrid>
                <ChoiceField
                  compact
                  label={t("settingsPage.field.defaultLayoutType.label")}
                  hint={t("settingsPage.field.defaultLayoutType.hint")}
                  value={layoutPreferences.arsenal_layout_style}
                  onSelect={(value) =>
                    setAppValue(
                      ["layoutPreferences", "arsenal_layout_style"],
                      value
                    )
                  }
                  choices={[
                    { label: t("settingsPage.choice.dualAngle"), value: "dual_angle" },
                    { label: "VLS", value: "vls" },
                    { label: "2LS", value: "2ls" },
                    { label: "PAL", value: "pal" },
                    { label: t("settingsPage.common.none"), value: "none" },
                  ]}
                />
                <ChoiceField
                  compact
                  label={t("settingsPage.field.dualAngleIncrements.label")}
                  hint={t("settingsPage.field.dualAngleIncrements.hint")}
                  value={layoutPreferences.dual_angle_measurement}
                  onSelect={(value) =>
                    setAppValue(
                      ["layoutPreferences", "dual_angle_measurement"],
                      value
                    )
                  }
                  choices={[
                    { label: "1°", value: "1" },
                    { label: "5°", value: "5" },
                  ]}
                />
              </FieldGrid>
            </Card>
          )}

          {activePage === "job-board" && hasJobBoard && (
            <>
              <Card>
                <CardHead>
                  <div>
                    <CardTitle>{t("settingsPage.card.jobBoardDefaults")}</CardTitle>
                  </div>
                </CardHead>

                <FieldGrid>
                  <ChoiceField
                    label={t("settingsPage.field.mandatorySerial.label")}
                    hint=""
                    value={plugin_settings_require_serial_number}
                    onSelect={(value) =>
                      updatePluginSetting("require_serial_number", value)
                    }
                    choices={[
                      { label: t("settingsPage.common.no"), value: false },
                      { label: t("settingsPage.common.yes"), value: true },
                    ]}
                  />
                  {/* Register -> job board: only a shop on HBS tells us about its sales. */}
                  {pluginSubscriptions.indexOf("SPECTRE_POS") >= 0 &&
                    provisionBackend === "hbs" && (
                      <ChoiceField
                        label={t("settingsPage.field.posJobsFromSales.label")}
                        hint={t("settingsPage.field.posJobsFromSales.hint")}
                        value={plugin_settings_pos_jobs_from_sales}
                        onSelect={(value) =>
                          updatePluginSetting("pos_jobs_from_sales", value)
                        }
                        choices={[
                          { label: t("settingsPage.common.no"), value: false },
                          { label: t("settingsPage.common.yes"), value: true },
                        ]}
                      />
                    )}
                  <ChoiceField
                    label={t("settingsPage.field.displayPrices.label")}
                    hint=""
                    value={plugin_settings_job_board_include_price}
                    onSelect={(value) =>
                      updatePluginSetting("job_board_include_price", value)
                    }
                    choices={[
                      { label: t("settingsPage.common.no"), value: false },
                      { label: t("settingsPage.common.yes"), value: true },
                    ]}
                  />
                  <FieldCard $compact>
                    <FieldLabel>{t("settingsPage.field.currency.label")}</FieldLabel>
                    <MetricInput
                      value={plugin_settings_job_board_currency}
                      onChange={(event) =>
                        updatePluginSetting(
                          "job_board_currency",
                          event.target.value
                        )
                      }
                      minLength={1}
                      debounceTimeout={300}
                    />
                  </FieldCard>
                  <FieldCard $compact>
                    <FieldLabel>{t("settingsPage.field.refreshTimer.label")}</FieldLabel>
                    <MetricInput
                      type="number"
                      value={plugin_settings_job_board_timer}
                      onChange={(event) =>
                        updatePluginSetting("job_board_timer", event.target.value)
                      }
                      minLength={1}
                      debounceTimeout={300}
                    />
                  </FieldCard>
                </FieldGrid>
              </Card>

              <Card>
                <CardHead>
                  <div>
                    <CardTitle>{t("settingsPage.card.serviceList")}</CardTitle>
                  </div>
                </CardHead>

                <ActionBar>
                  {!isReordering && (
                    <ActionButton
                      type="button"
                      $secondary
                      onClick={() =>
                        setShowJobServiceModal({ showJobServiceModal: true })
                      }
                    >
                      {t("settingsPage.common.add")}
                    </ActionButton>
                  )}
                  {!isReordering && (
                    <ActionButton
                      type="button"
                      $secondary
                      onClick={() => setIsReordering(true)}
                    >
                      {t("settingsPage.common.reorder")}
                    </ActionButton>
                  )}
                  {isReordering &&
                    (updating ? (
                      <UpdatingSpinner width={36} height={36} fill="#00187e" />
                    ) : (
                      <ActionButton type="button" onClick={handleReordering}>
                        {t("settingsPage.common.save")}
                      </ActionButton>
                    ))}
                </ActionBar>

                {!isReordering && (
                  <ListTable>
                    <TableHeader
                      $template={
                        hasPos ? "1.5fr 0.8fr 0.9fr auto" : "1.7fr 0.8fr auto"
                      }
                    >
                      <div>{t("settingsPage.table.service")}</div>
                      <div>{t("settingsPage.table.price")}</div>
                      {hasPos && <div>{t("settingsPage.table.sku")}</div>}
                      <div />
                    </TableHeader>
                    {movedServices.map((jobService) => (
                      <TableRow
                        key={jobService.id}
                        $template={
                          hasPos
                            ? "1.5fr 0.8fr 0.9fr auto"
                            : "1.7fr 0.8fr auto"
                        }
                      >
                        <RowValue>{jobService.name}</RowValue>
                        <RowValue>{jobService.price}</RowValue>
                        {hasPos && <RowValue>{jobService?.sku || "-"}</RowValue>}
                        <RowActions>
                          <SmallAction
                            type="button"
                            $danger
                            onClick={() => deleteJobService({ jobService })}
                          >
                            {t("settingsPage.common.delete")}
                          </SmallAction>
                          <SmallAction
                            type="button"
                            onClick={() =>
                              setShowJobEditServiceModal({
                                showJobEditServiceModal: true,
                                jobService,
                              })
                            }
                          >
                            {t("settingsPage.common.edit")}
                          </SmallAction>
                        </RowActions>
                      </TableRow>
                    ))}
                  </ListTable>
                )}

                {isReordering && (
                  <div>
                    {movedServices.map((jobService, index) => (
                      <DraggableService
                        key={jobService.id || index}
                        jobService={jobService}
                        index={index}
                        moveService={moveService}
                      />
                    ))}
                  </div>
                )}
              </Card>

              <Card>
                <CardHead>
                  <div>
                    <CardTitle>{t("settingsPage.card.salesPersonList")}</CardTitle>
                  </div>
                </CardHead>
                <ActionBar>
                  <ActionButton
                    type="button"
                    $secondary
                    onClick={() =>
                      setShowSalespersonModal({ showSalespersonModal: true })
                    }
                  >
                    {t("settingsPage.common.add")}
                  </ActionButton>
                </ActionBar>
                <ListTable>
                  <TableHeader $template="1fr auto">
                    <div>{t("settingsPage.table.salesPerson")}</div>
                    <div />
                  </TableHeader>
                  {movedSalesPersons.map((salesPerson) => (
                    <TableRow key={salesPerson.id} $template="1fr auto">
                      <RowValue>{salesPerson.name}</RowValue>
                      <RowActions>
                        <SmallAction
                          type="button"
                          $danger
                          onClick={() => {
                            deleteSalesPerson({ id: salesPerson.id });
                            setMovedSalesPersons(
                              R.reject(R.propEq("id", salesPerson.id))(
                                movedSalesPersons
                              )
                            );
                          }}
                        >
                          {t("settingsPage.common.delete")}
                        </SmallAction>
                        <SmallAction
                          type="button"
                          onClick={() =>
                            setShowSalespersonEditModal({
                              showSalespersonEditModal: true,
                              salesPerson,
                            })
                          }
                        >
                          {t("settingsPage.common.edit")}
                        </SmallAction>
                      </RowActions>
                    </TableRow>
                  ))}
                </ListTable>
              </Card>

              <Card>
                <CardHead>
                  <div>
                    <CardTitle>{t("settingsPage.card.locationList")}</CardTitle>
                  </div>
                </CardHead>
                <ActionBar>
                  <ActionButton
                    type="button"
                    $secondary
                    onClick={() =>
                      setShowLocationModal({ showLocationModal: true })
                    }
                  >
                    {t("settingsPage.common.add")}
                  </ActionButton>
                </ActionBar>
                <ListTable>
                  <TableHeader $template="1fr auto">
                    <div>{t("settingsPage.table.location")}</div>
                    <div />
                  </TableHeader>
                  {movedLocations.map((location) => (
                    <TableRow key={location.id} $template="1fr auto">
                      <RowValue>{location.name}</RowValue>
                      <RowActions>
                        <SmallAction
                          type="button"
                          $danger
                          onClick={() => {
                            deleteLocation({ id: location.id });
                            setMovedLocations(
                              R.reject(R.propEq("id", location.id))(movedLocations)
                            );
                          }}
                        >
                          {t("settingsPage.common.delete")}
                        </SmallAction>
                        <SmallAction
                          type="button"
                          onClick={() =>
                            setShowLocationEditModal({
                              showLocationEditModal: true,
                              location,
                            })
                          }
                        >
                          {t("settingsPage.common.edit")}
                        </SmallAction>
                      </RowActions>
                    </TableRow>
                  ))}
                </ListTable>
              </Card>
            </>
          )}

          {activePage === "bowler-plus" && hasBowlerPlus && (
            <>
              <Card>
                <ChoiceField
                  compact
                  label={t("settingsPage.field.signatureRequired.label")}
                  hint=""
                  value={plugin_settings_require_signature}
                  onSelect={(value) =>
                    updatePluginSetting("require_signature", value)
                  }
                  choices={[
                    { label: t("settingsPage.common.no"), value: false },
                    { label: t("settingsPage.common.yes"), value: true },
                  ]}
                />
              </Card>

              <Card>
                <CardHead>
                  <div>
                    <CardTitle>
                      {t("settingsPage.card.bowlingCenters")}
                    </CardTitle>
                    <CardSubtitle>
                      {t("settingsPage.card.bowlingCentersHint")}
                    </CardSubtitle>
                  </div>
                </CardHead>
                <ActionBar>
                  <MetricInput
                    debounceTimeout={0}
                    value={newBowlingCenter}
                    placeholder={t("settingsPage.field.bowlingCenter.label")}
                    onChange={(e) => setNewBowlingCenter(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addBowlingCenter();
                      }
                    }}
                  />
                  <ActionButton type="button" onClick={addBowlingCenter}>
                    {t("settingsPage.common.add")}
                  </ActionButton>
                </ActionBar>
                <ListTable>
                  {bowlingCenters.map((center) => (
                    <TableRow key={center} $template="1fr auto">
                      <RowValue>{center}</RowValue>
                      <RowActions>
                        <SmallAction
                          type="button"
                          $danger
                          onClick={() => removeBowlingCenter(center)}
                        >
                          {t("settingsPage.common.delete")}
                        </SmallAction>
                      </RowActions>
                    </TableRow>
                  ))}
                </ListTable>
              </Card>
            </>
          )}

          {activePage === "arsenal-plus" && hasArsenalPlus && (
            <Card>
              <ChoiceField
                compact
                label={t("settingsPage.field.layoutConversion.label")}
                hint=""
                value={plugin_settings_convert_layouts}
                onSelect={(value) => updatePluginSetting("convert_layouts", value)}
                choices={[
                  { label: t("settingsPage.common.no"), value: false },
                  { label: t("settingsPage.common.yes"), value: true },
                ]}
                link={{
                  href: PAGE_LINKS.layoutPreferences,
                  label: t("settingsPage.common.openWiki"),
                }}
              />
            </Card>
          )}

          {activePage === "pos" && hasPos && (
            <Card>
              <CardHead>
                <div>
                  <CardTitle>{t("settingsPage.card.linksToSites")}</CardTitle>
                </div>
              </CardHead>
              {!provisionStatus ||
              (provisionStatus !== "syncing" &&
                provisionStatus !== "syncing_catalog" &&
                provisionStatus !== "provisioning") ||
              provisionStatus === "ready" ? (
                <SpectreposStatusPanel />
              ) : (
                <ProvisioningProgress />
              )}
            </Card>
          )}

          {activePage === "pos" &&
            pluginSubscriptions.indexOf("SPECTRE_POS") >= 0 && (
              <Card>
                <CardHead>
                  <div>
                    <CardTitle>{t("settingsPage.card.posSwitches")}</CardTitle>
                  </div>
                </CardHead>
                <FieldGrid>
                  <ChoiceField
                    label={t("settingsPage.field.posSendFromSpectre.label")}
                    hint={t("settingsPage.field.posSendFromSpectre.hint")}
                    value={plugin_settings_pos_send_from_spectre}
                    onSelect={(value) =>
                      updatePluginSetting("pos_send_from_spectre", value)
                    }
                    choices={[
                      { label: t("settingsPage.common.no"), value: false },
                      { label: t("settingsPage.common.yes"), value: true },
                    ]}
                  />
                  {hasJobBoard && provisionBackend === "hbs" && (
                    <ChoiceField
                      label={t("settingsPage.field.posJobsFromSales.label")}
                      hint={t("settingsPage.field.posJobsFromSales.hint")}
                      value={plugin_settings_pos_jobs_from_sales}
                      onSelect={(value) =>
                        updatePluginSetting("pos_jobs_from_sales", value)
                      }
                      choices={[
                        { label: t("settingsPage.common.no"), value: false },
                        { label: t("settingsPage.common.yes"), value: true },
                      ]}
                    />
                  )}
                </FieldGrid>
              </Card>
            )}

          {activePage === "help" && (
            <Card>
              <LinkGrid>
                <LinkCard
                  href={PAGE_LINKS.introVideo}
                  target="_blank"
                  rel="noreferrer"
                >
                  <LinkTag>{t("settingsPage.help.video")}</LinkTag>
                  <LinkLabel>{t("settingsPage.help.introTitle")}</LinkLabel>
                </LinkCard>
                <LinkCard
                  href={PAGE_LINKS.gettingStarted}
                  target="_blank"
                  rel="noreferrer"
                >
                  <LinkTag>{t("settingsPage.help.wiki")}</LinkTag>
                  <LinkLabel>{t("settingsPage.help.gettingStarted")}</LinkLabel>
                </LinkCard>
                <LinkCard
                  href={PAGE_LINKS.tabletAtDrillPress}
                  target="_blank"
                  rel="noreferrer"
                >
                  <LinkTag>{t("settingsPage.help.guide")}</LinkTag>
                  <LinkLabel>{t("settingsPage.help.tetherTablet")}</LinkLabel>
                </LinkCard>
                <LinkCard
                  href={PAGE_LINKS.drillPressCalibration}
                  target="_blank"
                  rel="noreferrer"
                >
                  <LinkTag>{t("settingsPage.help.guide")}</LinkTag>
                  <LinkLabel>{t("settingsPage.help.drillPressCalibration")}</LinkLabel>
                </LinkCard>
                <LinkCard
                  href={PAGE_LINKS.ovalCalculator}
                  target="_blank"
                  rel="noreferrer"
                >
                  <LinkTag>{t("settingsPage.help.guide")}</LinkTag>
                  <LinkLabel>{t("settingsPage.help.ovalCalculation")}</LinkLabel>
                </LinkCard>
                <LinkCard
                  href={PAGE_LINKS.manual}
                  target="_blank"
                  rel="noreferrer"
                >
                  <LinkTag>{t("settingsPage.help.wiki")}</LinkTag>
                  <LinkLabel>{t("settingsPage.help.userManual")}</LinkLabel>
                </LinkCard>
                {helpCards
                  .filter((card) => card.label === "e-Learning")
                  .map((card) => (
                    <LinkCard
                      key={card.label}
                      href={card.href}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <LinkTag>{t("settingsPage.help.learning")}</LinkTag>
                      <LinkLabel>{t("settingsPage.help.eLearning")}</LinkLabel>
                      <LinkMeta>{t("settingsPage.help.eLearningMeta")}</LinkMeta>
                    </LinkCard>
                  ))}
              </LinkGrid>
            </Card>
          )}

          {activePage === "referrals" && (
            <Card>
              <SectionBlock>
                <div>
                  <SectionTitle>{t("settingsPage.section.code")}</SectionTitle>
                  <SectionHint>
                    {t("settingsPage.referrals.instruction")} ABC123
                  </SectionHint>
                </div>
                <ReferralCode>ABC123</ReferralCode>
                <LinkMeta>
                  {t("settingsPage.referrals.meta")}
                </LinkMeta>
              </SectionBlock>
            </Card>
          )}
        </Content>
      </Shell>
      <ModalJobService />
      <ModalJobEditService />
      <ModalSalesPerson />
      <ModalSalespersonEdit />
      <ModalLocation />
      <ModalLocationEdit />
    </PageWrap>
    </DndProvider>
  );
};

export const SettingsScreen = connect(
  (state) => ({
    initialValues: state.settings.appSettings,
    initialLanguage: state.settings.language,
    pluginSubscriptions: state.plugin.pluginSubscriptions,
    plugin_settings_job_board_include_price:
      state.plugin.plugin_settings.job_board_include_price,
    plugin_settings_job_board_currency:
      state.plugin.plugin_settings.job_board_currency,
    plugin_settings_job_board_timer:
      state.plugin.plugin_settings.job_board_timer,
    plugin_settings_convert_layouts:
      state.plugin.plugin_settings.convert_layouts,
    plugin_settings_require_signature:
      state.plugin.plugin_settings.require_signature,
    plugin_settings_require_serial_number:
      state.plugin.plugin_settings.require_serial_number,
    plugin_settings_pos_send_from_spectre:
      state.plugin.plugin_settings.pos_send_from_spectre !== false,
    plugin_settings_pos_jobs_from_sales:
      state.plugin.plugin_settings.pos_jobs_from_sales !== false,
    provisionBackend: state.plugin.provisionBackend,
    plugin_settings: state.plugin.plugin_settings,
    jobServices: state.plugin.jobServices,
    salesPersons: state.plugin.salesPersons,
    locations: state.plugin.locations,
    language: state.settings.language,
    updating: state.plugin.updating,
    provisionStatus: state.plugin.provisionStatus,
  }),
  {
    settingsHandleForm,
    settingsChangeLanguage,
    updateSharedSettings,
    setShowJobServiceModal,
    setShowSalespersonModal,
    setShowSalespersonEditModal,
    setShowLocationModal,
    setShowLocationEditModal,
    setShowJobEditServiceModal,
    deleteJobService,
    updateJobServices,
    deleteSalesPerson,
    deleteLocation,
  }
)(SettingsRedux);
